import { useMutation } from "@tanstack/vue-query";
import NoteAttachmentService, { UploadOptions } from "@/services/note-attachment";
import { NoteAttachment } from "@/dtos/Note.dto";

const STALL_TIMEOUT_MS = 120_000;
const STALL_TICK_MS = 5_000;

const extractErrorMessage = (e: any): string => {
  if (e?.s3?.code) return `${e.s3.code}: ${e.s3.message ?? "upload rejected"}`;
  const status = e?.response?.status;
  const serverMsg = e?.response?.data?.msg ?? e?.response?.data?.errorMessage ?? null;
  if (serverMsg) return serverMsg;
  if (status === 403) return "Upload rejected by storage — please retry.";
  return e?.message ?? "Upload failed";
};

interface UploadCommon {
  files: File[];
  onProgress?: UploadOptions["onProgress"];
  signal?: AbortSignal;
}

export interface DraftUploadVars extends UploadCommon {
  draftKey: string;
}

export interface NoteUploadVars extends UploadCommon {
  noteId: string;
}

async function uploadOne(
  file: File,
  ctx: { draftKey: string } | { noteId: string },
  opts: UploadOptions
): Promise<NoteAttachment> {
  const presignRes =
    "draftKey" in ctx
      ? await NoteAttachmentService.presignDraft(ctx.draftKey, file)
      : await NoteAttachmentService.presignNote(ctx.noteId, file);
  if (!presignRes.data.status) {
    throw new Error(presignRes.data.msg || "Presign failed");
  }
  const { uploadUrl, storageKey } = presignRes.data.data;

  let lastProgressAt = Date.now();
  const internal = new AbortController();
  if (opts.signal) {
    if (opts.signal.aborted) internal.abort();
    else opts.signal.addEventListener("abort", () => internal.abort(), { once: true });
  }

  const stallTimer = window.setInterval(() => {
    if (Date.now() - lastProgressAt > STALL_TIMEOUT_MS) {
      internal.abort(new DOMException("Upload stalled", "AbortError"));
    }
  }, STALL_TICK_MS);

  try {
    await NoteAttachmentService.putToPresignedUrl(uploadUrl, file, {
      signal: internal.signal,
      onProgress: (pct) => {
        lastProgressAt = Date.now();
        opts.onProgress?.(pct);
      },
    });
  } finally {
    window.clearInterval(stallTimer);
  }

  const confirmRes =
    "draftKey" in ctx
      ? await NoteAttachmentService.confirmDraft(ctx.draftKey, {
          storageKey,
          fileName: file.name,
        })
      : await NoteAttachmentService.confirmNote(ctx.noteId, {
          storageKey,
          fileName: file.name,
        });
  if (!confirmRes.data.status) {
    throw new Error(confirmRes.data.msg || "Confirm failed");
  }
  return confirmRes.data.data;
}

export function useUploadDraftNoteAttachment() {
  return useMutation({
    retry: 0,
    mutationFn: async (vars: DraftUploadVars): Promise<NoteAttachment[]> => {
      const out: NoteAttachment[] = [];
      try {
        for (const file of vars.files) {
          const row = await uploadOne(
            file,
            { draftKey: vars.draftKey },
            { onProgress: vars.onProgress, signal: vars.signal }
          );
          out.push(row);
        }
      } catch (e: any) {
        if (e?.code === "ERR_CANCELED" || e?.name === "AbortError") throw e;
        throw new Error(extractErrorMessage(e));
      }
      return out;
    },
  });
}

export function useUploadNoteAttachment() {
  return useMutation({
    retry: 0,
    mutationFn: async (vars: NoteUploadVars): Promise<NoteAttachment[]> => {
      const out: NoteAttachment[] = [];
      try {
        for (const file of vars.files) {
          const row = await uploadOne(
            file,
            { noteId: vars.noteId },
            { onProgress: vars.onProgress, signal: vars.signal }
          );
          out.push(row);
        }
      } catch (e: any) {
        if (e?.code === "ERR_CANCELED" || e?.name === "AbortError") throw e;
        throw new Error(extractErrorMessage(e));
      }
      return out;
    },
  });
}

export function useDeleteNoteDraftAttachments() {
  return useMutation({
    mutationFn: async (draftKey: string): Promise<void> => {
      const result = await NoteAttachmentService.deleteDraft(draftKey);
      if (!result.data.status) {
        throw new Error(result.data.msg || "Failed to discard draft attachments");
      }
    },
  });
}
